import { useMemo } from 'react';
import { useAuth } from '../components/auth/AuthProvider';

// Role constants
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  USER: 'USER'
};

// Permission constants
export const PERMISSIONS = {
  MANAGE_USERS: 'manage:users',
  APPROVE_USERS: 'approve:users',
  ASSIGN_ROLES: 'assign:roles',
  VIEW_ADMIN_DASHBOARD: 'view:admin_dashboard',
  EDIT_TEAMS: 'edit:teams',
  VIEW_ALL_TEAMS: 'view:all_teams'
};

export function useRole() {
  const { role, loading, approved } = useAuth();

  const roleChecks = useMemo(() => {
    if (!approved || !role) {
      return {
        isSuperAdmin: false,
        isAdmin: false,
        isUser: false,
        hasAdminAccess: false
      };
    }

    const isSuperAdmin = role === ROLES.SUPER_ADMIN;
    const isAdmin = role === ROLES.ADMIN || isSuperAdmin;
    const isUser = role === ROLES.USER || isAdmin;
    const hasAdminAccess = isSuperAdmin || isAdmin;

    return {
      isSuperAdmin,
      isAdmin,
      isUser,
      hasAdminAccess
    };
  }, [role, approved]);

  const hasPermission = useMemo(() => {
    return (permission) => {
      if (!approved || !role) return false;

      switch (permission) {
        case PERMISSIONS.MANAGE_USERS:
        case PERMISSIONS.ASSIGN_ROLES:
          return roleChecks.isSuperAdmin;
        case PERMISSIONS.APPROVE_USERS:
        case PERMISSIONS.VIEW_ADMIN_DASHBOARD:
        case PERMISSIONS.EDIT_TEAMS:
        case PERMISSIONS.VIEW_ALL_TEAMS:
          return roleChecks.hasAdminAccess;
        default:
          return false;
      }
    };
  }, [roleChecks, approved, role]);

  return {
    role,
    loading,
    approved,
    ...roleChecks,
    hasPermission
  };
}